import React, { useState } from "react";
import toast from "react-hot-toast";
import { useFirestoreAdmin } from "../context/FirestoreAdminProvider";
import { runQuery } from "../services/queryBuilderService";
import { trackRead } from "../services/monitorService";
import { cellText } from "../utils/firestoreValueUtils";

const OPS = ["==", "!=", "<", "<=", ">", ">=", "arrayContains", "arrayContainsAny", "in", "notIn"];

export default function QueryBuilderPage() {
  const { collectionName, selectCollection, allCollections } = useFirestoreAdmin();
  const [clauses, setClauses] = useState([{ field: "", op: "==", value: "" }]);
  const [sortField, setSortField] = useState("");
  const [sortDir, setSortDir] = useState("asc");
  const [pageLimit, setPageLimit] = useState(50);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const rows = await runQuery(collectionName, clauses, sortField, sortDir, pageLimit);
      trackRead(rows.length);
      setResults(rows);
      toast.success(`Loaded ${rows.length} documents`);
    } catch (e) {
      toast.error(e?.message || "Query failed — check indexes and field types");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-2xl font-bold">Query Builder</h1>
        <p className="text-sm text-slate-600">Visual Firestore query with live preview.</p>
      </header>
      <div className="fa-card space-y-3 p-4">
        <select className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm" value={collectionName} onChange={(e) => selectCollection(e.target.value)}>
          {allCollections.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        {clauses.map((c, i) => (
          <div key={i} className="flex flex-wrap gap-2">
            <input className="rounded-lg border px-2 py-1.5 text-sm" placeholder="Field" value={c.field} onChange={(e) => {
              const next = [...clauses];
              next[i] = { ...c, field: e.target.value };
              setClauses(next);
            }} />
            <select className="rounded-lg border px-2 py-1.5 text-sm" value={c.op} onChange={(e) => {
              const next = [...clauses];
              next[i] = { ...c, op: e.target.value };
              setClauses(next);
            }}>
              {OPS.map((op) => <option key={op} value={op}>{op}</option>)}
            </select>
            <input className="rounded-lg border px-2 py-1.5 text-sm" placeholder="Value" value={c.value} onChange={(e) => {
              const next = [...clauses];
              next[i] = { ...c, value: e.target.value };
              setClauses(next);
            }} />
          </div>
        ))}
        <button type="button" className="fa-btn" onClick={() => setClauses([...clauses, { field: "", op: "==", value: "" }])}>Add clause</button>
        <div className="flex flex-wrap gap-2">
          <input className="rounded-lg border px-2 py-1.5 text-sm" placeholder="orderBy field" value={sortField} onChange={(e) => setSortField(e.target.value)} />
          <select className="rounded-lg border px-2 py-1.5 text-sm" value={sortDir} onChange={(e) => setSortDir(e.target.value)}>
            <option value="asc">asc</option>
            <option value="desc">desc</option>
          </select>
          <input type="number" className="w-24 rounded-lg border px-2 py-1.5 text-sm" value={pageLimit} onChange={(e) => setPageLimit(Number(e.target.value))} />
          <button type="button" className="fa-btn fa-btn-primary" onClick={run} disabled={loading}>{loading ? "Running…" : "Run query"}</button>
        </div>
      </div>
      <div className="fa-card fa-table-wrap">
        <table className="fa-table">
          <thead><tr><th>ID</th><th>Preview</th></tr></thead>
          <tbody>
            {results.map((r) => (
              <tr key={r.id}>
                <td className="font-mono text-xs">{r.id}</td>
                <td className="max-w-lg truncate text-xs">{cellText(Object.values(r.data || {})[0])}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
